import React, { Component } from 'react';
import Lozenge from '@atlaskit/lozenge';
import Tooltip from '@atlaskit/tooltip';
// eslint-disable-next-line import/order
import Link from '@atlassian/jira-common-components-link';

import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { injectIntlV2 as injectIntl } from '@atlassian/jira-intl/src/v2/inject.tsx';
import type { Intl } from '@atlassian/jira-shared-types/src/general.tsx';
import { CI_CD_GET_STARTED_LINK, CYCLE_TIME_REPORT_KEY } from '../../../common/constant';
import type { ReportKey } from '../../../model/types';
import messages from './messages';
import {
	DisabledContainer,
	Title,
	Header,
	Body,
	TextWrapper,
	Description,
	ReasonWrapper,
	itemWidthGridUnits,
	headerHeightGridUnits,
	EnabledLinkContainer,
} from './styled';

export type Props = {
	isApplicable: boolean;
	isNew: boolean;
	inapplicableReason: {
		reason: string;
		description: string;
	} | null;
	reportKey: ReportKey;
	description: string;
	name: string;
	imageUri: string;
	url: string;
};

export type PropsWithIntl = Props & Intl;

const generateReportImage = (name: string, imageUri: string, isApplicable: boolean) => {
	const headerWidth = gridSize * itemWidthGridUnits;
	const headerHeight = gridSize * headerHeightGridUnits;

	// IE needs to be treated differently to get its images in grayscale. See
	// https://stackoverflow.com/questions/14813142/internet-explorer-10-howto-apply-grayscale-filter/14818991#14818991
	const image = isApplicable ? (
		<image
			x="0"
			y="0"
			width={headerWidth}
			height={headerHeight}
			xmlnsXlink="http://www.w3.org/1999/xlink"
			xlinkHref={imageUri}
			// @ts-expect-error - TS2322 - Type '{ x: string; y: string; width: number; height: number; xmlnsXlink: string; xlinkHref: string; alt: string; }' is not assignable to type 'SVGProps<SVGImageElement>'.
			alt={name}
		/>
	) : (
		<image
			filter='url("#grayscaleReportsImageForIE11")'
			x="0"
			y="0"
			width={headerWidth}
			height={headerHeight}
			xmlnsXlink="http://www.w3.org/1999/xlink"
			xlinkHref={imageUri}
			// @ts-expect-error - TS2322 - Type '{ filter: string; x: string; y: string; width: number; height: number; xmlnsXlink: string; xlinkHref: string; alt: string; }' is not assignable to type 'SVGProps<SVGImageElement>'.
			alt={name}
		/>
	);

	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			viewBox={`0 0 ${headerWidth} ${headerHeight}`}
			width={headerWidth}
			height={headerHeight}
		>
			<defs>
				<filter id="grayscaleReportsImageForIE11">
					<feComposite
						result="inputTo_grayscaleReportImage"
						in="SourceGraphic"
						in2="SourceGraphic"
						operator="arithmetic"
						k1="0"
						k2="1"
						k3="0"
						k4="0"
					/>
					<feColorMatrix
						id="filter_grayscaleReportImage"
						type="saturate"
						values="0"
						data-filterid="grayscaleReportImage"
					/>
				</filter>
			</defs>
			{image}
		</svg>
	);
};

// eslint-disable-next-line jira/react/no-class-components
export class Item extends Component<PropsWithIntl> {
	static defaultProps = {
		isNew: false,
		inapplicableReason: null,
	};

	renderLozenge() {
		const { isApplicable, inapplicableReason, isNew, intl } = this.props;

		if (!isApplicable && inapplicableReason) {
			return (
				<ReasonWrapper>
					<Tooltip content={inapplicableReason.description}>
						<Lozenge appearance="new">{inapplicableReason.reason}</Lozenge>
					</Tooltip>
				</ReasonWrapper>
			);
		}

		if (isNew) {
			return (
				<ReasonWrapper>
					<Tooltip content={intl.formatMessage(messages.newLabel)}>
						<Lozenge appearance="new" isBold>
							{intl.formatMessage(messages.newLabel)}
						</Lozenge>
					</Tooltip>
				</ReasonWrapper>
			);
		}

		return null;
	}

	render() {
		const { reportKey, inapplicableReason, name, description, imageUri, isApplicable, intl } =
			this.props;

		const Container = isApplicable ? EnabledLinkContainer : DisabledContainer;
		const hrefIfNeeded = isApplicable ? { href: this.props.url } : null;

		const reportImage = generateReportImage(name, imageUri, isApplicable);

		return (
			<Container {...hrefIfNeeded}>
				<Header>
					{/* <Image src={imageUri} alt={name} isApplicable={isApplicable} /> */}
					{reportImage}
					{this.renderLozenge()}
				</Header>
				<Body>
					<TextWrapper>
						<Title>{name}</Title>
						<Description>{description}</Description>
						{inapplicableReason && reportKey === CYCLE_TIME_REPORT_KEY && (
							// @ts-expect-error - TS2322 - Type '{ children: string; href: string; target: string; }' is not assignable to type 'IntrinsicAttributes & Props'.
							<Link href={CI_CD_GET_STARTED_LINK} target="_blank">
								{intl.formatMessage(messages.learnMore)}
							</Link>
						)}
					</TextWrapper>
				</Body>
			</Container>
		);
	}
}

export default injectIntl(Item);
